import { FC, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckoutView } from '@/app/_checkout';
import type {
  CheckoutCopy,
  CheckoutData,
  CompanyInfo,
  OrderSummaryModel,
} from '@/app/_checkout';
import { useInitializeInvoiceService } from './services/use-initialize-invoice-service';
import { useGatewayConfigService } from './services/use-gateway-config-service';
import {
  CONTRACT_PAYMENT_METHODS_KEY,
  useContractPaymentMethodsService,
} from './services/use-contract-payment-methods-service';
import { useMeService } from './services/use-me-service';
import { usePayInvoiceService } from './services/use-pay-invoice-service';
import type { InvoiceCompany } from './types';
import ScheduledPaymentNote from './components/scheduled-payment-note';
import { isInvoiceSettled } from '@/utils/invoice-checkout-state';

const resolveCompany = (raw?: InvoiceCompany | { data?: InvoiceCompany }): CompanyInfo => {
  const company = (raw && 'data' in raw ? raw.data : raw) as InvoiceCompany | undefined;
  return {
    name: company?.name ?? '',
    email: company?.email ?? '',
    phone: company?.phone ?? '',
    logo: company?.logo ?? null,
  };
};

const InvoicePayPage: FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation(['invoice-pay']);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token') ?? '';
  const lang = searchParams.get('lang') ?? '';

  useEffect(() => {
    // The JWT arrives via the URL (?token=). axiosInstance attaches
    // sessionStorage.jwt_token as the Bearer header, so it must be stored
    // before this authenticated request fires.
    if (token) sessionStorage.setItem("jwt_token", token);
  }, [token]);

  const { invoice = '' } = useParams<{ invoice: string }>();
  const {
    data,
    isLoading: invoiceLoading,
    error: invoiceError,
  } = useInitializeInvoiceService(invoice);
  const payMutation = usePayInvoiceService(invoice);

  const invoiceData = data?.data;
  const contractId = invoiceData?.relationships?.contract?.data?.id;

  // Gateway config and saved cards are fetched separately, keyed on the
  // invoice's contract.
  const {
    data: gatewayConfig,
    error: gatewayError,
  } = useGatewayConfigService(contractId);
  const {
    data: paymentMethods,
    error: paymentMethodsError,
  } = useContractPaymentMethodsService(contractId);

  // Billing details for the add-card form come from the authenticated client
  // (not contract-scoped), so this runs as soon as the JWT is available.
  const { data: billing, error: billingError } = useMeService(token);

  // While the contract is known but its dependent queries haven't resolved
  // yet, keep showing the skeleton — the payment panel can't render without
  // the gateway config or the saved cards.
  const contractDepsPending =
    !!contractId &&
    ((gatewayConfig === undefined && !gatewayError) ||
      (paymentMethods === undefined && !paymentMethodsError));
  const billingPending = !!token && billing === undefined && !billingError;
  const isLoading = invoiceLoading || contractDepsPending || billingPending;
  const error = invoiceError ?? gatewayError ?? paymentMethodsError ?? billingError;

  useEffect(() => {
    if (lang) i18n.changeLanguage(lang);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkoutData = useMemo<CheckoutData | undefined>(() => {
    if (!invoiceData) return undefined;
    const contract = invoiceData.relationships?.contract?.data;
    return {
      gatewayConfig: gatewayConfig as CheckoutData['gatewayConfig'],
      paymentMethods: paymentMethods ?? [],
      billingInfo: billing ?? {
        first_name: '',
        last_name: '',
        email: '',
        country: 'CA',
        address: '',
      },
      contractId: contract?.id ?? '',
      // No gateway config means the account doesn't accept online payments →
      // the "payment unavailable" screen is shown instead of the panel.
      paymentAvailable: invoiceData.attributes.is_payable && !!gatewayConfig,
      // A link lives seven days in an inbox: the one opened after the invoice was paid says
      // so, rather than telling the client the company takes no cards.
      settled: isInvoiceSettled(invoiceData.attributes),
      company: resolveCompany(contract?.relationships?.company),
      invalidateKey: [CONTRACT_PAYMENT_METHODS_KEY, contractId],
    };
  }, [invoiceData, gatewayConfig, paymentMethods, billing, contractId, invoice]);

  const summary = useMemo<OrderSummaryModel | undefined>(() => {
    if (!invoiceData) return undefined;
    const { attributes, relationships } = invoiceData;
    const lineItems = relationships?.lineItems?.data ?? [];
    // Invoice + line-item money arrives in cents; formatCurrency expects major
    // units (dollars), so convert everything here.
    const toMajor = (cents: number) => cents / 100;
    return {
      lines: lineItems.map((item) => ({
        label: item.attributes.description,
        sublabel:
          item.attributes.quantity > 1
            ? t('invoice-pay:line-item-quantity', 'Qty: {{quantity}}', {
                quantity: item.attributes.quantity,
              })
            : undefined,
        amount: toMajor(item.attributes.amount),
      })),
      subtotal: toMajor(attributes.subtotal),
      taxes: (attributes.taxes?.data ?? []).map((tax) => ({
        ...tax,
        amount: toMajor(tax.amount),
      })),
      total: toMajor(attributes.total),
    };
  }, [invoiceData, t]);

  const scheduledPayment = invoiceData?.attributes.scheduled_payment;
  const company = checkoutData?.company.name ?? '';
  const copy = useMemo<CheckoutCopy>(() => ({
    successTitle: t('invoice-pay:success-title', 'Payment Successful!'),
    successDescription: t(
      'invoice-pay:success-description',
      'Thank you for your payment. Your invoice has been paid.',
    ),
    alreadyPaidTitle: t('invoice-pay:already-paid-title', 'Already Paid'),
    alreadyPaidDescription: t(
      'invoice-pay:already-paid-description',
      'This invoice has already been paid.',
    ),
    unavailableSubtitle: t(
      'invoice-pay:payment-unavailable-subtitle',
      "{{company}} Doesn't Accept Online Payments",
      { company },
    ),
    unavailableDescription: t(
      'invoice-pay:payment-unavailable-description',
      'To pay this invoice, contact {{company}} directly using the options below.',
      { company },
    ),
    unavailableReference: t(
      'invoice-pay:payment-unavailable-reference',
      'Please mention this invoice when reaching out so they can assist you faster.',
    ),
  }), [t, company]);

  return (
    <CheckoutView
      isLoading={isLoading}
      error={error}
      data={checkoutData}
      summary={summary}
      summaryNote={scheduledPayment ? <ScheduledPaymentNote scheduledPayment={scheduledPayment} /> : undefined}
      payMutation={payMutation}
      copy={copy}
    />
  );
};

export default InvoicePayPage;
